"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IkeModelCollection = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const id_1 = require("../../id");
const collection_data_1 = require("./collection.data");
const collection_loader_1 = require("./collection.loader");
const capture_provider_1 = require("./fields/provider/capture.provider");
const collection_provider_1 = require("./fields/provider/collection.provider");
const collection_model_read_1 = require("./serializer/collection.model.read");
const ReadUtil = tslib_1.__importStar(require("./serializer/collection.model.util"));
const collection_model_write_1 = require("./serializer/collection.model.write");
const collection_upgrade_1 = require("./upgrade/collection.upgrade");
class IkeModelCollection {
    loader() {
        return new collection_loader_1.IkeCollectionLoaderFromState();
    }
    upgrade(collections, targetForm) {
        return new collection_upgrade_1.IkeCollectionUpgrade(collections, targetForm);
    }
    create(form, loader) {
        const collection = new collection_data_1.CollectionModel(form, loader);
        collection.state.uuid = id_1.IkeId.v4();
        collection.state.revision = collection.state.instanceId = id_1.IkeId.generate();
        collection.state.createdAt = collection.state.updatedAt = new Date();
        return collection;
    }
    read(collection, schema, loader, log) {
        const reader = new collection_model_read_1.IkeModelCollectionReader(collection, schema, loader);
        if (log) {
            reader.log = log;
        }
        return reader.read();
    }
    write(model) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const collectedAt = yield model.getCollectedAt();
            const collectionJSON = {
                objectId: model.id,
                v: IkeModelCollection.JSON_VERSION_LATEST,
                uuid: model.uuid,
                data: {
                    recordeduuid: model.uuid,
                    uuid: model.schema.rootFormId,
                    version: model.schema.revision,
                    data: collection_model_write_1.IkeModelCollectionWriter.writeData(model)
                },
                formuuid: model.schema.rootFormId,
                formversion: model.schema.revision,
                jobuuid: model.jobId,
                measurements: collection_model_write_1.IkeModelCollectionWriter.writeCollectionMeasurements(model),
                brightnessCorrections: collection_model_write_1.IkeModelCollectionWriter.writeCollectionBrightnessCorrections(model),
                cropParameters: collection_model_write_1.IkeModelCollectionWriter.writeCollectionCropParameters(model),
                imageVisibilities: collection_model_write_1.IkeModelCollectionWriter.writeImageVisibilities(model),
                revision: model.revision,
                collectedBy: model.collectedBy,
                collectedWith: model.collectedWith,
                assignedTo: model.assignedTo,
                collectedAt: core_api_1.ParseJSON.toDate(collectedAt),
                createdAt: model.createdAt != null ? model.createdAt.toISOString() : null,
                updatedAt: model.updatedAt != null ? model.updatedAt.toISOString() : null,
                updatedBy: model.updatedBy
            };
            if (collectedAt !== model.createdAt && collectedAt != null) {
                collectionJSON.data.createdat = collectedAt.getTime() / 1000;
            }
            return collectionJSON;
        });
    }
    /**
     * Build a collection summary from a collection model.
     *
     * TODO: `link`, `updatedBy`, and `tags` are not fully implemented.
     */
    summary(model) {
        const summaryObject = {
            objectId: model.id,
            collectionId: model.id,
            revision: model.revision,
            uuid: model.uuid,
            formRevision: model.schema.revision,
            createdAt: model.createdAt.toISOString(),
            updatedAt: model.updatedAt.toISOString(),
            collectedAt: core_api_1.ParseJSON.toDate(model.collectedAt),
            collectedBy: model.collectedBy,
            collectedWith: model.collectedWith,
            assignedTo: model.assignedTo,
            links: collection_provider_1.CollectionProvider.getCollectionIds(model),
            updatedBy: [model.updatedBy],
            title: model.title,
            subtitle: model.subtitle,
            siteType: model.siteType,
            dropPole: model.dropPole,
            poleLoading: model.poleLoading,
            mapMarkerColor: model.mapMarkerColor,
            status: null,
            location: core_api_1.ParseJSON.toGeoPoint(model.location),
            captureIds: capture_provider_1.CaptureProvider.getCaptureIds(model),
            tags: [model.jobId]
        };
        const statusField = model.status;
        if (statusField != null) {
            if (statusField.hasValue()) {
                summaryObject.status = statusField.displayValue;
            }
        }
        return summaryObject;
    }
    getCaptureUUIDs(collection) {
        const collectionCaptureData = ReadUtil.findObjectsWhere(collection, ReadUtil.isCaptureData);
        const uuidMap = {};
        for (const captureData of collectionCaptureData) {
            const uuid = captureData.camera.imageuuid;
            if (uuid != null && !uuidMap[uuid]) {
                uuidMap[uuid] = true;
            }
        }
        return Object.keys(uuidMap);
    }
    getLinkUUIDs(collection) {
        const collectionLinkData = ReadUtil.findObjectsWhere(collection, ReadUtil.isLink);
        const uuidMap = {};
        for (const linkData of collectionLinkData) {
            const uuid = linkData.collectionUuid;
            if (uuid != null && !uuidMap[uuid]) {
                uuidMap[uuid] = true;
            }
        }
        return Object.keys(uuidMap);
    }
    getCalibrationIds(captures) {
        const calibrationIdMap = {};
        for (const capture of captures) {
            const calibrationId = capture.calibrationid;
            if (calibrationId != null && !calibrationIdMap[calibrationId]) {
                calibrationIdMap[calibrationId] = true;
            }
        }
        return Object.keys(calibrationIdMap);
    }
    getFileIds(collection) {
        const collectionFileData = ReadUtil.findObjectsWhere(collection, ReadUtil.isFileData);
        const idMap = {};
        for (const fileData of collectionFileData) {
            const id = fileData.attachmentId;
            if (id != null && !idMap[id]) {
                idMap[id] = true;
            }
        }
        return Object.keys(idMap);
    }
}
exports.IkeModelCollection = IkeModelCollection;
/** v3 collections have had their captures extracted into the capture file table */
IkeModelCollection.JSON_VERSION_3_PARSE_API = 3;
/** v4 Collections have re-mapped their captures to include the objectId and raw values of the actual capture. */
IkeModelCollection.JSON_VERSION_4_CAPTURE_FIXED = 4;
/** v5 Collections  */
IkeModelCollection.JSON_VERSION_5_BRIGHTNESS_CORRECTION_ADDED = 5;
/** v6 Collections  */
IkeModelCollection.JSON_VERSION_6_CROP_PARAMETER_ADDED = 6;
IkeModelCollection.JSON_VERSION_LATEST = 6;
//# sourceMappingURL=collection.model.js.map